import React, { useEffect } from "react";
import {
  fetchFeeStructures,
  addFeeStructure,
  updateFeeStructure,
  deleteFeeStructure,
} from "../../redux/feeStructureSlice";
import { fetchClassesV1 } from "../../redux/classSlice";
import { fetchCourses } from "../../redux/coursesSlice";
import { Pencil, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import FeeStructureManagementModal from "../../modals/admin/FeeStructureManagementModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchCampuses } from "../../redux/campusesSlice";
import { Toaster } from "react-hot-toast";

function FeeStructureManagement() {
  const dispatch = useDispatch();
  const { feeStructures, loading, pagination } = useSelector((state) => state.feeStructures);
  const { campuses } = useSelector((state) => state?.campuses || []);
  const classes = useSelector((state) => state.classes.classes || []);
  const { courses } = useSelector((state) => state.courses);

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editingData, setEditingData] = React.useState(null);
  const [expandedRowIndex, setExpandedRowIndex] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const PAGE_SIZE = 50;

  const totalPages = pagination?.totalPages ?? 1;

  useEffect(() => {
    dispatch(fetchFeeStructures({ pageNumber: currentPage, pageSize: PAGE_SIZE }));
  }, [dispatch, currentPage]);

  useEffect(() => {
    dispatch(fetchClassesV1({ pageNumber: 1, pageSize: 100 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchCourses({ pageNumber: 1, pageSize: 100 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchCampuses());
  }, [dispatch]);

  const handleAddNew = () => {
    setEditingData(null);
    setIsModalOpen(true);
  };

  const handleSave = async (data) => {
    if (editingData) {
      await dispatch(updateFeeStructure({ id: editingData.id, feeStructure: data }));
    } else {
      await dispatch(addFeeStructure(data));
      // After adding, jump to page 1 so the new record is visible
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        dispatch(fetchFeeStructures({ pageNumber: 1, pageSize: PAGE_SIZE }));
      }
    }
    setIsModalOpen(false);
    setEditingData(null);
    dispatch(fetchFeeStructures({ pageNumber: currentPage, pageSize: PAGE_SIZE }));
  };

  const handleEdit = (index) => {
    setEditingData({ ...feeStructures[index], index });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    await dispatch(deleteFeeStructure(id));
    // If we deleted the last item on a page beyond page 1, go back one page
    const remainingOnPage = feeStructures.length - 1;
    if (remainingOnPage === 0 && currentPage > 1) {
      setCurrentPage((p) => p - 1);
    } else {
      dispatch(fetchFeeStructures({ pageNumber: currentPage, pageSize: PAGE_SIZE }));
    }
  };

  const toggleView = (index) => {
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Toaster />
      <div className="flex w-full items-center justify-between mt-4 space-x-2">
        <input
          type="text"
          placeholder="Search..."
          className="border border-gray-300 rounded p-2 w-3/4"
        />
        <button
          onClick={handleAddNew}
          className="px-4 py-2 bg-[#241763] w-1/4 text-white rounded"
        >
          + New Fee Structure
        </button>
      </div>

      <div className="overflow-x-auto mt-6">
        <table className="table-auto w-full mt-6">
          <thead className="py-4 border-b font-bold">
            <tr className="bg-gray-100 border-b">
              <th>Name</th>
              <th>Description</th>
              <th>Fee Code</th>
              <th>Tuition Fee</th>
              <th>Lab Fee</th>
              <th>Academic Year</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {feeStructures.length > 0 ? (
              feeStructures.map((fee, index) => (
                <React.Fragment key={fee.id}>
                  <tr className="hover:bg-gray-200">
                    <td>{fee.name}</td>
                    <td>{fee.description}</td>
                    <td>{fee.feeCode}</td>
                    <td>
                      {fee.currency} {fee.tutionFee}
                    </td>
                    <td>{fee.labFee}</td>
                    <td>{fee.createdDateTime}</td>
                    <td className="space-x-2 flex items-center py-2">
                      <button
                        className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                        onClick={() => handleEdit(index)}
                      >
                        <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                      </button>
                      <button
                        className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                        onClick={() => handleDelete(fee.id)}
                      >
                        <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                      </button>
                      <div
                        onClick={() => toggleView(index)}
                        className="cursor-pointer bg-[#B6A5C9]/50 p-2 rounded hover:bg-[#241763]/60 group"
                      >
                        {expandedRowIndex === index ? (
                          <ChevronUp
                            size={16}
                            className="group-hover:text-white"
                          />
                        ) : (
                          <ChevronDown
                            size={16}
                            className="group-hover:text-white"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedRowIndex === index && (
                    <tr>
                      <td colSpan="7">
                        <div className="bg-gray-100 p-4">
                          <p><strong>Billing Frequency:</strong> {fee.billingFrequency}</p>
                          <p><strong>Billing Cycle:</strong> {fee.billingCycleDate}</p>
                          <p><strong>Lab Fee:</strong> {fee.currency} {fee.labFee}</p>
                          <p><strong>Library Fee:</strong> {fee.currency} {fee.libraryFee}</p>
                          <p><strong>Sports Fee:</strong> {fee.currency} {fee.sportsFee}</p>
                          <p><strong>Software Charges:</strong> {fee.currency} {fee.lmsFee}</p>
                          <p><strong>Admission Fee:</strong> {fee.currency} {fee.admissionFee}</p>
                          <p><strong>Security Deposit:</strong> {fee.currency} {fee.securityFee}</p>
                          <p><strong>Optional Fee:</strong> {fee.currency} {fee.optionalFee}</p>
                          <p><strong>Meal Plan:</strong> {fee.currency} {fee.mealFee}</p>
                          <p><strong>Fines:</strong> {fee.currency} {fee.schoolFines}</p>
                          <p><strong>Total Fee:</strong> {fee.currency} {fee.totalFee}</p>
                          <p><strong>Late Payment Fine:</strong> {fee.currency} {fee.latePaymentFine}</p>
                          <p><strong>Taxes:</strong> {fee.currency} {fee.taxesFee}</p>
                          <p><strong>Total Payable:</strong> {fee.currency} {fee.totalPaybale}</p>
                          <h3 className="font-bold mt-2">Discounts:</h3>
                          {fee.discounts &&
                            fee.discounts.map((discount, i) => (
                              <p key={i}>
                                {discount.name}: {discount.discountPercentage}%
                              </p>
                            ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center p-4">
                  No Fee Structures Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-4">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          className="px-3 py-1 bg-[#241763] text-white rounded"
          disabled={currentPage === 1}
        >
          Previous
        </button>

        <div className="flex">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1 mx-1 border rounded ${
                currentPage === i + 1
                  ? "bg-[#241763] text-white"
                  : "bg-gray-200"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          className="px-3 py-1 bg-[#241763] text-white rounded"
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {isModalOpen && (
        <FeeStructureManagementModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          editingData={editingData}
          classes={classes}
          courses={courses}
          campuses={campuses}
        />
      )}
    </div>
  );
}

export default FeeStructureManagement;